import qs from 'qs'
import { SetStateAction, Dispatch } from 'react'
import { useDispatch } from 'react-redux'
import { useMutation } from '@apollo/client'
import { ExclamationCircleFilled } from '@ant-design/icons'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { updateImDfmFeedbackRefetchQuery } from '@Admin/AdminQuotingPage/ImDfmSlidesPanel/ImDfmSlide/ActiveSlide/updateImDfmFeedbackInfoMutation'
import { SLIDE_QUERY_PARAM } from '@Admin/AdminQuotingPage/constants'

import Box from '~/core/Box'
import Button from '~/core/Button'
import getColor from '~/lib/getColor'
import logger from '~/lib/logger'
import { deleteImDfm3dSlide } from '~/redux-modules/unsaved-im-dfm-3d-slides/actions'
import { DeleteImDfm3dSlideValuesType } from '~/redux-modules/unsaved-im-dfm-3d-slides/reducer'

import destroyDfmFeedbackMutation from './destroyDfmFeedbackMutation'

const PLACEMENT = 'delete-slide-popover'

type ParamsType = {
    id?: string
}

type PropsType = {
    dfmFeedbackId?: string | null
    idx: string
    setActiveSlideIndex?: Dispatch<SetStateAction<number>>
    togglePopover: () => void
    setDeleteKeyClicked?: Dispatch<SetStateAction<boolean>>
    isUnsaved?: boolean | null
}

const ConfirmDeletePopoverContent = (props: PropsType) => {
    const {
        dfmFeedbackId,
        idx,
        setActiveSlideIndex,
        togglePopover,
        setDeleteKeyClicked,
        isUnsaved,
    } = props

    const dispatch = useDispatch()
    const navigate = useNavigate()
    const location = useLocation()
    const { id } = useParams<ParamsType>()

    const [destroyDfmFeedback] = useMutation(destroyDfmFeedbackMutation)

    const slideIdx = parseInt(idx)

    const selectSlide = (selectedSlideId: number) => {
        const currentQueryParams = qs.parse(location.search)
        const newQueryParams = {
            ...currentQueryParams,
        }

        newQueryParams[SLIDE_QUERY_PARAM] = selectedSlideId.toString()
        navigate(
            {
                search: qs.stringify(newQueryParams).substring(3),
            },
            { replace: true },
        )
    }

    const deleteDfmSlide = async (evt: Event) => {
        if (isUnsaved) {
            dispatch(
                deleteImDfm3dSlide({
                    qmrId: id,
                    slideId: dfmFeedbackId,
                } as DeleteImDfm3dSlideValuesType),
            )
            if (setActiveSlideIndex) {
                setActiveSlideIndex(slideIdx)
                selectSlide(slideIdx === 1 ? slideIdx : slideIdx - 1)
            }
        }

        if (setDeleteKeyClicked) {
            setDeleteKeyClicked(false)
        }

        evt.stopPropagation()
        togglePopover()
        try {
            await destroyDfmFeedback({
                variables: {
                    dfmFeedbackId,
                },
                refetchQueries: [
                    {
                        query: updateImDfmFeedbackRefetchQuery,
                        variables: { id },
                    },
                ],
            })

            if (setActiveSlideIndex) {
                setActiveSlideIndex(slideIdx)
                selectSlide(slideIdx === 1 ? slideIdx : slideIdx - 1)
            }
        } catch (err) {
            logger.exception(err, {
                extra: {
                    info: 'ConfirmDeletePopoverContent::Delete slide function failed on destroyDfmFeedback mutation',
                    qmrId: id,
                    dfmFeedbackId,
                },
            })
        }
    }

    return (
        <>
            <Box>
                <ExclamationCircleFilled
                    style={{
                        color: getColor('warning'),
                        fontSize: '14px',
                        marginRight: '12px',
                        marginTop: '4px',
                    }}
                />
                <Box height="55px" width="270px">
                    Are you sure you want to delete this slide? This action
                    cannot be undone.
                </Box>
            </Box>
            <Box marginTop="8px" justifyContent="end">
                <Button
                    data-test-target="close-popover"
                    name="cancel-delete-slide"
                    onClick={togglePopover}
                    placement={`${PLACEMENT}-close`}
                    size="small"
                    style={{
                        fontWeight: 400,
                        marginRight: '4px',
                    }}
                >
                    Cancel
                </Button>
                <Button
                    data-test-target="delete-slide"
                    onClick={deleteDfmSlide}
                    name="delete-slide"
                    placement={`${PLACEMENT}-selected`}
                    size="small"
                    style={{ fontWeight: 400 }}
                    type="danger"
                >
                    Delete slide
                </Button>
            </Box>
        </>
    )
}

export default ConfirmDeletePopoverContent
